import { useDrag } from '@use-gesture/react'
import { Activity, createContext, type ReactNode, Suspense, use, useEffect, useLayoutEffect, useRef, useState } from 'react'
import {
  createPath,
  type Location,
  type NavigationType,
  type RouteObject,
  useLocation,
  useNavigate,
  useNavigationType,
  useRoutes,
} from 'react-router'
import { useNavigate as useScopedNavigate } from './scope'
import classes from './stack.module.css'

/**
 * A phone's pages as a native app's: a page opened slides in over the last one, which stays mounted underneath
 * (React `<Activity>`), so going back shows it exactly as it was left (scroll, typed text, open filters) without
 * loading it again. Back is the page's own back button, the system back or a swipe from the left edge. Each tab (a
 * bottom item's page, an app) keeps its own stack, like a native app's tabs: opening another and coming back finds the
 * pages as they were left, and opening the one in front again goes back to its first page. The router and its URLs stay as they are: this only
 * decides which pages are kept and how they move.
 */

interface Entry {
  /** Stays the same while the page lives, through a replace (filters in the URL) or a back to it */
  id: string
  /** The history entry it is on now */
  key: string
  location: Location
  /**
   * The history entry before it is not the page under it (its tab was brought back from another, or a link went back
   * to it over pages that are still in history): back goes to the page under it by replacing this entry
   */
  detached?: true
}
type Role = 'top' | 'under' | 'hidden' | 'leaving'

interface Stack {
  /** The menu item it started from, `''` when it started elsewhere (a link from a notification) */
  root: string
  entries: Entry[]
}

interface StackState {
  stack: Stack
  /** The other menu items' stacks as they were left, the latest first */
  tabs: Stack[]
  leaving: Entry | null
  /** How the pages move to where their role puts them: another tab's pages just appear */
  motion: 'push' | 'pop' | 'none'
}

interface StackEntry {
  /** The page in front, the one a person is using: only it blocks leaving with unsaved changes */
  top: boolean
  /** To the page under this one, if there is one (false otherwise) */
  back?: () => boolean
  /**
   * Whether going from `current` to `next` takes this page away, so unsaved changes should ask first: not when it isn't
   * the page being left, nor when `next` is another menu item, whose own stack this one is kept beside
   */
  leaves: (current: Location, next: Location) => boolean
}
const EntryContext = createContext<StackEntry>({ top: true, leaves: () => true })
/** Where the page sits in the phone's stack; outside one (a computer) every page is in front */
export const useStackEntry = () => use(EntryContext)

/**
 * Back to where one really was before this page: on a phone the page under it in its stack, else (and on a computer) the
 * browser's back, to whatever page that was, in any tab or app; `fallback` only when there is nothing before it (the
 * first page opened, from a notification or a link from outside)
 */
export function useGoBack() {
  // `fallback` is written from the app's own root (`/waybills`)
  const navigate = useScopedNavigate()
  const { back } = useStackEntry()
  return (fallback: string) => {
    if (back?.()) return
    if ((window.history.state?.idx ?? 0) > 0) navigate(-1)
    else navigate(fallback)
  }
}

/** The history state of a page opened from a menu (`data-menu` on its link): a menu item, which keeps its own stack */
export const fromMenu = { menu: true }
const isMenu = (at: Location) => (at.state as { menu?: unknown } | null)?.menu === true

const MAX_KEPT = 10
const MAX_TABS = 5
const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)'
const sameUrl = (a: Location, b: Location) => a.pathname === b.pathname && a.search === b.search
const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches

function slide(el: HTMLElement, from: number, to: number, done?: () => void) {
  for (const a of el.getAnimations()) a.cancel()
  el.style.transform = to ? `translateX(${to}px)` : ''
  if (from === to || reduced()) return done?.()
  const animation = el.animate([{ transform: `translateX(${from}px)` }, { transform: `translateX(${to}px)` }], {
    duration: 360,
    easing: EASE,
  })
  animation.onfinish = () => done?.()
}

// The stacks survive a reload of the same history entry (pull to refresh, a new version of the app)
const storeKey = (app: string) => `tnn.stack.${app}`
function restore(app: string, location: Location): StackState | undefined {
  try {
    const saved = JSON.parse(sessionStorage.getItem(storeKey(app)) ?? 'null') as Pick<StackState, 'stack' | 'tabs'> | null
    const last = saved?.stack?.entries?.at(-1)
    if (!saved || last?.key !== location.key) return undefined
    const entries = [...saved.stack.entries.slice(0, -1), { ...last, location }]
    return { stack: { ...saved.stack, entries }, tabs: saved.tabs ?? [], leaving: null, motion: 'none' }
  } catch {
    return undefined
  }
}
function persist(app: string, { stack, tabs }: StackState) {
  try {
    sessionStorage.setItem(storeKey(app), JSON.stringify({ stack, tabs }))
  } catch {}
}
const scrolls = new Map<string, number>()

/** The stack in front becomes `stack`; the one it replaces is kept with the other tabs */
const switchTo = (s: StackState, stack: Stack): StackState => ({
  stack,
  tabs: [s.stack, ...s.tabs].filter((t) => t.root !== stack.root).slice(0, MAX_TABS - 1),
  leaving: null,
  motion: 'none',
})

interface Move {
  next: StackState
  /** A tab brought back shows its own page: the address follows it */
  follow?: Location
  /** The menu item in front opened again on its first page: that page goes back to its top */
  toTop?: string
}

/**
 * What a navigation does to the stacks. `name`: the tab of this address (`''` for none), naming a stack that starts
 * here; `menu`: the tab of the menu it was opened from, when it was
 */
function advance(
  s: StackState,
  type: NavigationType,
  location: Location,
  name: string,
  menu: string | undefined,
  backTo: string | null,
): Move {
  const here: Entry = { id: location.key, key: location.key, location }
  const list = s.stack.entries
  const top = list.at(-1)
  const under = list.at(-2)
  const on = (entries: Entry[]) => ({ ...s, stack: { ...s.stack, entries } })
  if (!top) return { next: switchTo(s, { root: name, entries: [here] }) }

  if (type === 'REPLACE') {
    // Back from a page whose history entry before it isn't the page under it
    if (backTo && under?.id === backTo && sameUrl(under.location, location))
      return { next: { ...on([...list.slice(0, -2), { ...under, key: here.key, location }]), leaving: top, motion: 'pop' } }
    const again = sameUrl(top.location, location) && list.length === 1 && !!s.stack.root
    return { next: on([...list.slice(0, -1), { ...top, key: here.key, location }]), toTop: again ? top.id : undefined }
  }

  // Back (or a link to a page already underneath, like a list's "back to the list"): the pages above it go
  const at = list.findLastIndex((e) => (type === 'POP' && e.key === here.key) || sameUrl(e.location, location))
  if (at >= 0) {
    const found = list[at] ?? top
    // A link (not a back) leaves the pages above in history: the history entry before is not the page under it
    const kept = { ...found, key: here.key, location, ...(type === 'PUSH' && { detached: true as const }) }
    if (at === list.length - 1) return { next: on([...list.slice(0, -1), kept]), toTop: list.length === 1 && menu ? top.id : undefined }
    return { next: { ...on([...list.slice(0, at), kept]), leaving: top, motion: 'pop' } }
  }

  if (type === 'POP') {
    // Back to a page of another tab: that tab comes to the front at that page
    const tab = s.tabs.find((t) => t.entries.some((e) => e.key === here.key))
    const i = tab ? tab.entries.findLastIndex((e) => e.key === here.key) : -1
    const page = tab?.entries[i]
    if (tab && page) return { next: switchTo(s, { root: tab.root, entries: [...tab.entries.slice(0, i), { ...page, location }] }) }
    return { next: switchTo(s, { root: name, entries: [here] }) }
  }

  if (menu !== undefined) {
    if (menu === s.stack.root) {
      const first = list[0] ?? top
      // Another page of the tab's own menu (an app's): the tab starts again there
      if (first.location.pathname !== location.pathname) return { next: { ...on([here]), leaving: null, motion: 'none' } }
      // The menu item in front at another address (its filters): back to its first page
      return { next: { ...on([{ ...first, key: here.key, location }]), leaving: list.length > 1 ? top : null, motion: 'pop' } }
    }
    // Another menu item: its pages as they were left, or a new stack
    const saved = s.tabs.find((t) => t.root === menu)
    if (!saved?.entries.length) return { next: switchTo(s, { root: menu, entries: [here] }) }
    const shown = saved.entries.at(-1) ?? here
    const entries =
      saved.entries.length > 1 ? [...saved.entries.slice(0, -1), { ...shown, detached: true as const }] : [{ ...shown, key: here.key }]
    return { next: switchTo(s, { root: menu, entries }), follow: sameUrl(shown.location, location) ? undefined : shown.location }
  }

  const next = [...list, here]
  return {
    next: { ...on(next.length > MAX_KEPT ? [...next.slice(0, 1), ...next.slice(-MAX_KEPT + 1)] : next), leaving: null, motion: 'push' },
  }
}

export interface StackOutletProps {
  app: string
  routes: RouteObject[]
  /**
   * The tab a page is in (`''`: none), each with its own stack: a menu's link (`data-menu`) brings its tab to the front as
   * it was left, or starts it there. Thananan OS: each bottom item's page is one, and each app with all its pages
   */
  tabOf: (at: Location) => string
  fallback: ReactNode
}

export function StackOutlet({ app, routes, tabOf, fallback }: StackOutletProps) {
  const location = useLocation()
  const type = useNavigationType()
  const navigate = useNavigate()
  const menuOf = (at: Location) => (isMenu(at) ? tabOf(at) || at.pathname + at.search : undefined)
  const [state, setState] = useState<StackState>(() => {
    const restored = restore(app, location)
    if (restored) return restored
    return {
      stack: { root: tabOf(location), entries: [{ id: location.key, key: location.key, location }] },
      tabs: [],
      leaving: null,
      motion: 'none',
    }
  })
  const latest = useRef(state)
  latest.current = state
  const seen = useRef(location.key)
  /** The page a back by replacing is going to */
  const backing = useRef<string | null>(null)
  const box = useRef<HTMLDivElement>(null)
  const els = useRef(new Map<string, HTMLDivElement>())

  // biome-ignore lint/correctness/useExhaustiveDependencies: acts once per navigation, whatever else re-renders
  useLayoutEffect(() => {
    if (seen.current === location.key) return
    seen.current = location.key
    const name = tabOf(location)
    const menu = menuOf(location)
    const backTo = backing.current
    backing.current = null
    const move = advance(latest.current, type, location, name, menu, backTo)
    setState((s) => (s === latest.current ? move.next : advance(s, type, location, name, menu, backTo).next))
    if (move.follow) navigate(createPath(move.follow), { replace: true, state: move.follow.state })
    if (move.toTop) els.current.get(move.toTop)?.scrollTo({ top: 0, behavior: reduced() ? 'auto' : 'smooth' })
  }, [location.key])

  useEffect(() => persist(app, state), [app, state])

  /** To the page under the one in front: the browser's back, or a replace when its history entry before is not that page */
  const back = () => {
    const entries = latest.current.stack.entries
    const top = entries.at(-1)
    const under = entries.at(-2)
    if (!top || !under) return false
    if (!top.detached) navigate(-1)
    else {
      backing.current = under.id
      navigate(createPath(under.location), { replace: true, state: under.location.state })
    }
    return true
  }
  const keeps = (next: Location) => {
    const menu = menuOf(next)
    return menu !== undefined && menu !== latest.current.stack.root
  }

  const xs = useRef(new Map<string, number>())
  const roles = useRef(new Map<string, Role>())
  const { stack, tabs, leaving } = state
  const entries = stack.entries
  // Other tabs' pages first and hidden, then the stack in front, the page leaving over it
  const items: { entry: Entry; role: Role }[] = tabs.flatMap((t) => t.entries.map((entry) => ({ entry, role: 'hidden' as const })))
  entries.forEach((entry, i) => {
    items.push({ entry, role: i === entries.length - 1 ? 'top' : i === entries.length - 2 ? 'under' : 'hidden' })
  })
  if (leaving) items.push({ entry: leaving, role: 'leaving' })

  // Each page moves from where it is to where its role puts it: in from the right, aside a little, out to the right
  useLayoutEffect(() => {
    const width = box.current?.clientWidth ?? 0
    const firstPaint = roles.current.size === 0
    const next = new Map(items.map((item) => [item.entry.id, item.role]))
    for (const [key, role] of next) {
      if (roles.current.get(key) === role) continue
      const el = els.current.get(key)
      if (!el) continue
      const to = role === 'top' ? 0 : role === 'leaving' ? width : -0.3 * width
      const from =
        state.motion === 'none' || role === 'hidden'
          ? to
          : (xs.current.get(key) ?? (role === 'top' && !firstPaint && state.motion === 'push' ? width : to))
      const done = role === 'leaving' ? () => setState((s) => (s.leaving?.id === key ? { ...s, leaving: null } : s)) : undefined
      slide(el, from, to, done)
      xs.current.set(key, to)
    }
    for (const key of xs.current.keys()) if (!next.has(key)) xs.current.delete(key)
    roles.current = next
  })

  // The browser tab (and a window's bubble) says the page in front: a page names itself when it mounts, so one that
  // comes back to the front gets the name it had back
  const titles = useRef(new Map<string, string>())
  const frontId = entries.at(-1)?.id
  const front = useRef(frontId)
  front.current = frontId
  useEffect(() => {
    const remember = () => front.current && titles.current.set(front.current, document.title)
    remember()
    const observer = new MutationObserver(remember)
    observer.observe(document.head, { subtree: true, childList: true, characterData: true })
    return () => observer.disconnect()
  }, [])
  useLayoutEffect(() => {
    const named = frontId && titles.current.get(frontId)
    if (named && document.title !== named) document.title = named
  }, [frontId])

  // Swipe from the left edge to go back: the page follows the finger, the one under it comes back into view
  const bind = useDrag(
    ({ first, last, initial: [x0], movement: [mx], velocity: [vx], direction: [dx], cancel, event }) => {
      if (entries.length < 2 || leaving || !box.current) return cancel()
      const rect = box.current.getBoundingClientRect()
      if (first && (x0 - rect.left > 28 || (event.target as Element).closest('[data-no-swipe]'))) return cancel()
      const top = entries.at(-1)
      const under = entries.at(-2)
      const topEl = top && els.current.get(top.id)
      const underEl = under && els.current.get(under.id)
      if (!top || !under || !topEl || !underEl) return cancel()
      const width = rect.width
      const x = Math.max(0, Math.min(width, mx))
      const ux = -0.3 * width + 0.3 * x
      if (first) for (const el of [topEl, underEl]) for (const a of el.getAnimations()) a.cancel()
      if (!last) {
        topEl.style.transform = `translateX(${x}px)`
        underEl.style.transform = `translateX(${ux}px)`
        xs.current.set(top.id, x)
        xs.current.set(under.id, ux)
        return
      }
      const snapBack = () => {
        slide(topEl, xs.current.get(top.id) ?? x, 0)
        slide(underEl, xs.current.get(under.id) ?? ux, -0.3 * width)
        xs.current.set(top.id, 0)
        xs.current.set(under.id, -0.3 * width)
      }
      if (x > width * 0.35 || (vx > 0.35 && dx > 0 && x > 24)) {
        back()
        // Unsaved changes ask first: staying puts the page back
        window.setTimeout(() => roles.current.get(top.id) === 'top' && snapBack(), 400)
      } else snapBack()
    },
    // No `filterTaps`: on touch events alone a mouse never makes a tap, so it would stop every click on a screen that
    // also takes touch; a swipe makes no click anyway. Its 3px before a drag starts stays
    { axis: 'x', threshold: 3, pointer: { touch: true } },
  )

  return (
    <div ref={box} className={classes.stack} {...bind()}>
      {items.map(({ entry, role }) => (
        <Activity key={entry.id} mode={role === 'hidden' ? 'hidden' : 'visible'}>
          <Page
            entry={entry}
            role={role}
            routes={routes}
            fallback={fallback}
            context={{
              top: role === 'top',
              back,
              leaves: (current, next) => role === 'top' && current.key === entry.key && !keeps(next),
            }}
            setEl={(el) => {
              if (el) els.current.set(entry.id, el)
              else els.current.delete(entry.id)
            }}
          />
        </Activity>
      ))}
    </div>
  )
}

function Page({
  entry,
  role,
  routes,
  fallback,
  context,
  setEl,
}: {
  entry: Entry
  role: Role
  routes: RouteObject[]
  fallback: ReactNode
  context: StackEntry
  setEl: (el: HTMLDivElement | null) => void
}) {
  const scroller = useRef<HTMLDivElement>(null)
  const element = useRoutes(routes, entry.location)
  // Back where it was left, also after being hidden deeper in the stack or in another tab (which forgets the scroll)
  useLayoutEffect(() => {
    const top = scrolls.get(entry.id)
    if (scroller.current && top) scroller.current.scrollTop = top
  }, [entry.id])
  return (
    <div
      ref={(el) => {
        scroller.current = el
        setEl(el)
      }}
      className={classes.page}
      data-role={role}
      inert={role !== 'top'}
      onScroll={(e) => scrolls.set(entry.id, e.currentTarget.scrollTop)}
    >
      <EntryContext value={context}>
        <Suspense fallback={fallback}>{element}</Suspense>
      </EntryContext>
    </div>
  )
}
